import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { supabase } from '@/integrations/supabase/client';
import logoSrc from '@/assets/snap-chart-logo-final.png';

const { FiAlertCircle, FiCheck, FiX, FiArrowLeft, FiZap, FiShield, FiTarget } = FiIcons;

const validatePassword = (password) => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
});

const PasswordRequirement = ({ met, text }) => (
  <div className={`flex items-center gap-2 text-xs ${met ? 'text-emerald-400' : 'text-gray-500'}`}>
    <SafeIcon icon={met ? FiCheck : FiX} className="w-3 h-3" />
    <span>{text}</span>
  </div>
);

const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
  const [agreeToRisk, setAgreeToRisk] = useState(false);
  const [agreeToCookies, setAgreeToCookies] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { user, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const passwordValidation = useMemo(() => validatePassword(password), [password]);
  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const allAgreed = agreeToTerms && agreeToPrivacy && agreeToRisk && agreeToCookies;

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!allAgreed) return setError('You must agree to all policies to continue');
    if (password !== confirmPassword) return setError('Passwords do not match');
    if (!isPasswordValid) return setError('Password does not meet all requirements');

    setLoading(true);
    try {
      await register(firstName, lastName, email, password);
      try {
        const { getStoredRefCode, clearStoredRefCode } = await import('@/hooks/useRefCapture');
        const ref = getStoredRefCode();
        if (ref) {
          supabase.rpc('attribute_signup', { p_ref_code: ref }).then(({ data, error }) => {
            if (!error && data?.attributed) clearStoredRefCode();
          }, () => { /* noop */ });
        }
      } catch { /* attribution is non-critical */ }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      });
      if (error) {
        setError(error.message);
        setLoading(false);
      }
    } catch (err) {
      console.error('Google Sign-In error:', err);
      setError('Failed to sign up with Google. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black text-white flex relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-40 w-[40rem] h-[40rem] bg-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -right-40 w-[40rem] h-[40rem] bg-cyan-600/10 rounded-full blur-3xl"></div>
      </div>

      {/* ═══════════ LEFT — Brand panel ═══════════ */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-[40%] relative z-10 flex-col justify-between p-12 border-r border-white/5">
        <div>
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-gray-500 hover:text-cyan-400 transition mb-12">
            <SafeIcon icon={FiArrowLeft} /> back to snap-chart.ai
          </Link>

          <div className="flex items-center gap-3 mb-10">
            <img src={logoSrc} alt="SnapChart AI" className="h-12 w-12" />
            <span className="font-bold text-xl bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              SnapChart AI
            </span>
          </div>

          <h2 className="text-4xl xl:text-5xl font-black tracking-tight leading-[1.05] mb-4">
            <span className="text-white">Get the AI</span><br />
            <span className="bg-gradient-to-r from-purple-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">that says NO.</span>
          </h2>
          <p className="text-base text-gray-400 leading-relaxed max-w-md">
            The first AI Risk Officer that vetoes your worst trades before you take them. 30 days free. No credit card.
          </p>
        </div>

        {/* Why-join checklist */}
        <div className="space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-600 mb-4">
            ↓ what you get instantly
          </div>
          {[
            { icon: FiZap, text: 'Built-in chart + one-click 📸 Capture (XAUUSD, BTC, NAS100, +)', color: 'cyan' },
            { icon: FiTarget, text: 'AI verdict in 2s · TRADE · NO-TRADE · WAIT', color: 'purple' },
            { icon: FiShield, text: 'TDI Score · counterfactual capital tracker · journal auto-saved', color: 'emerald' },
          ].map((b, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.05 }}
              className="flex items-start gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.02]">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${b.color === 'cyan' ? 'bg-cyan-500/15 text-cyan-400' : b.color === 'purple' ? 'bg-purple-500/15 text-purple-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                <SafeIcon icon={b.icon} className="text-base" />
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
          <p className="text-[11px] text-gray-600 font-mono pt-2">
            no broker login · no api key · just chart → verdict
          </p>
        </div>
      </div>

      {/* ═══════════ RIGHT — Form ═══════════ */}
      <div className="flex-1 relative z-10 flex flex-col">
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-white/5">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-gray-400 hover:text-cyan-400 transition">
            <SafeIcon icon={FiArrowLeft} /> back
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src={logoSrc} alt="" className="h-8 w-8" />
            <span className="font-bold text-sm bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">SnapChart AI</span>
          </Link>
          <div className="w-12" />
        </header>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="w-full max-w-md py-6"
          >
            {/* Mobile heading */}
            <div className="lg:hidden mb-8 text-center">
              <h1 className="text-3xl font-black tracking-tight mb-2">
                <span className="text-white">Get the AI </span>
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">that says NO.</span>
              </h1>
              <p className="text-sm text-gray-400">30 days free · no credit card</p>
            </div>

            <div className="hidden lg:block mb-6">
              <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400 mb-3">
                create your account
              </span>
              <h1 className="text-3xl font-black tracking-tight text-white">Start your free trial</h1>
              <p className="text-sm text-gray-400 mt-1">Takes 30 seconds. Accept the 4 policies below.</p>
            </div>

            {error && (
              <div className="flex items-center p-3 space-x-2 text-sm text-red-200 bg-red-500/10 rounded-lg border border-red-500/30 mb-4">
                <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              onClick={handleGoogleSignIn}
              type="button"
              disabled={!allAgreed}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white/[0.03] border border-white/10 rounded-xl hover:bg-white/[0.06] hover:border-white/20 transition-all duration-200 text-gray-200 font-medium disabled:opacity-40 disabled:cursor-not-allowed mb-3"
            >
              <SafeIcon icon={FcGoogle} className="w-5 h-5" />
              Continue with Google
            </button>
            {!allAgreed && (
              <p className="text-[11px] text-amber-400/80 text-center mb-4 font-mono">
                ↓ check the 4 boxes below to enable
              </p>
            )}

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/5"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 text-gray-600 font-mono uppercase tracking-[0.2em]">or with email</span>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-xs font-mono uppercase tracking-[0.15em] text-gray-400 mb-2">First name</label>
                  <input id="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    className="w-full py-3 px-4 text-white bg-gray-900/60 border border-white/10 rounded-xl focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400/50 outline-none transition-all placeholder:text-gray-600"
                    required />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-xs font-mono uppercase tracking-[0.15em] text-gray-400 mb-2">Last name</label>
                  <input id="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                    className="w-full py-3 px-4 text-white bg-gray-900/60 border border-white/10 rounded-xl focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400/50 outline-none transition-all placeholder:text-gray-600"
                    required />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-mono uppercase tracking-[0.15em] text-gray-400 mb-2">Email</label>
                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com"
                  className="w-full py-3 px-4 text-white bg-gray-900/60 border border-white/10 rounded-xl focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400/50 outline-none transition-all placeholder:text-gray-600"
                  required />
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-mono uppercase tracking-[0.15em] text-gray-400 mb-2">Password</label>
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full py-3 px-4 text-white bg-gray-900/60 border border-white/10 rounded-xl focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400/50 outline-none transition-all placeholder:text-gray-600"
                  required />
                {password.length > 0 && (
                  <div className="mt-2 p-3 bg-white/[0.03] rounded-lg border border-white/5 space-y-1">
                    <PasswordRequirement met={passwordValidation.minLength} text="Minimum 8 characters" />
                    <PasswordRequirement met={passwordValidation.hasUppercase} text="At least 1 uppercase letter (A-Z)" />
                    <PasswordRequirement met={passwordValidation.hasLowercase} text="At least 1 lowercase letter (a-z)" />
                    <PasswordRequirement met={passwordValidation.hasNumber} text="At least 1 number (0-9)" />
                    <PasswordRequirement met={passwordValidation.hasSpecial} text="At least 1 special character (!@#$%...)" />
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-xs font-mono uppercase tracking-[0.15em] text-gray-400 mb-2">Confirm password</label>
                <input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full py-3 px-4 text-white bg-gray-900/60 border border-white/10 rounded-xl focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400/50 outline-none transition-all placeholder:text-gray-600"
                  required />
              </div>

              {/* Compact agreements */}
              <div className="space-y-2.5 pt-2">
                {[
                  { state: agreeToTerms, set: setAgreeToTerms, label: 'Terms', href: '/terms', id: 'terms' },
                  { state: agreeToPrivacy, set: setAgreeToPrivacy, label: 'Privacy Policy', href: '/privacy', id: 'privacy' },
                  { state: agreeToRisk, set: setAgreeToRisk, label: 'Risk Disclaimer', href: '/risk-disclaimer', id: 'risk' },
                  { state: agreeToCookies, set: setAgreeToCookies, label: 'Cookies Policy', href: '/cookies', id: 'cookies' },
                ].map((p) => (
                  <div key={p.id} className="flex items-start gap-2">
                    <input id={p.id} type="checkbox" checked={p.state} onChange={(e) => p.set(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-cyan-400 bg-white/5 border-white/20 rounded" />
                    <label htmlFor={p.id} className="text-xs text-gray-300">
                      I agree to the <Link to={p.href} className="font-semibold text-cyan-400 hover:text-cyan-300">{p.label}</Link>
                    </label>
                  </div>
                ))}
              </div>

              <button
                type="submit"
                disabled={loading || !allAgreed}
                className="w-full py-4 text-base font-bold text-black bg-gradient-to-r from-cyan-400 to-purple-400 rounded-xl hover:scale-[1.01] active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-xl shadow-cyan-500/20"
              >
                {loading ? 'Creating account...' : 'Start 30-day free trial →'}
              </button>
            </form>

            <p className="text-sm text-center text-gray-400 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="font-bold text-cyan-400 hover:text-cyan-300">Log in →</Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
